"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType] = useState("Kitchen");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let valid = true;
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      valid = false;
    } else {
      setZipError("");
    }
    if (!consent) {
      setConsentError(true);
      valid = false;
    } else {
      setConsentError(false);
    }
    if (!valid) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const payload = new URLSearchParams({
        name: fullName,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "kitchen-remodeling-2-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-3xl mx-auto">
        <div ref={ref} style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(15px)",
        transition: "opacity 500ms ease-out, transform 500ms ease-out"
      }} className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 md:p-12">
          <div className="text-center max-w-xl mx-auto mb-8 sm:mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground font-normal tracking-tight mb-3">
              Request an estimate
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              Share details about your kitchen project below. Our team reviews your requirements and responds promptly with consultation availability.
            </p>
          </div>

          {submitSuccess ? <Alert className="border-border bg-background/50">
              <CheckCircle2 className="size-5 text-primary" />
              <AlertTitle className="text-foreground text-base">Request received</AlertTitle>
              <AlertDescription className="text-muted-foreground text-sm mt-1">
                Thank you for reaching out. We have received your project details and will be in touch shortly.
              </AlertDescription>
            </Alert> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {errorMessage && <Alert variant="destructive">
                  <AlertCircle className="size-5 text-destructive" />
                  <AlertTitle>Unable to submit request</AlertTitle>
                  <AlertDescription>
                    {errorMessage.includes("(470) 847-9076") ? <span>
                        Please call{" "}
                        <a href="tel:4708479076" className="underline font-medium">
                          (470) 847-9076
                        </a>{" "}
                        or email{" "}
                        <a href="mailto:serviceleadalerts@gmail.com" className="underline font-medium">
                          serviceleadalerts@gmail.com
                        </a>{" "}
                        to request an estimate.
                      </span> : errorMessage}
                  </AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="full-name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="full-name" name="name" type="text" required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Doe" className="bg-background/60" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone" name="phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(404) 555-0199" className="bg-background/60" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email" name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" className="bg-background/60" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip" name="zip" type="text" required value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} placeholder="30319" className="bg-background/60" aria-invalid={!!zipError} />
                  {zipError && <p className="text-xs text-destructive mt-1 font-medium">
                      {zipError}
                    </p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="project-type">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="project-type" name="project_type" value={projectType} disabled aria-readonly="true" className="flex h-9 w-full rounded-md border border-input bg-background/60 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-75 text-foreground">
                    {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timing">Preferred Timing</Label>
                  <select id="timing" name="timing" value={timing} onChange={e => setTiming(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/60 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 text-foreground">
                    <option value="">Select timing (optional)</option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget Range</Label>
                  <select id="budget" name="budget" value={budget} onChange={e => setBudget(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background/60 px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 text-foreground">
                    <option value="">Select budget (optional)</option>
                    {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-details">Project Details / Message</Label>
                <Textarea id="project-details" name="details" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us about your kitchen remodel goals, layout thoughts, or special requests." className="bg-background/60 min-h-[110px]" />
              </div>

              <div className="pt-1">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
                setConsent(checked === true);
                if (consentError && checked === true) {
                  setConsentError(false);
                }
              }} aria-invalid={consentError} className="mt-0.5" />
                  <Label htmlFor="consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive mt-1.5 font-medium ml-7">
                    Please indicate your consent before submitting.
                  </p>}
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto font-medium px-8 shadow-sm">
                  {isSubmitting ? "Submitting..." : "Request Estimate"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}